import React from 'react';
import {
  gql,
  useQuery,
  useMutation,
  useLazyQuery,
} from '@apollo/client';

import { useUser } from '../../../store/useUser';
import { UserMessages } from './userMessages';

const GET_USER_MESSAGE = gql`
  query GetUserMessage($id: ID!, $markRead: Boolean!) {
    getUserMessage(id: $id, markRead: $markRead) {
    recipients
    messages{
      id
      senderString      
      recipientString
      subject
      body
      createdAt    
      attachments {
        filename
        mimeType
        data
      }
    }
}}
`;

// GraphQL
const LIST_INBOX = gql`
  query ListInbox(
    $search: String,
    $limit: Int,
    $offset: Int,
    $filter: String
  ) {
    listUserInboxMessages(
      search: $search,
      limit: $limit,
      offset: $offset,
      filter: $filter
    ) {
      messages {
        id
        senderId
        senderString
        recipientId
        recipientString
        subject
        isRead
        isPinned
        isDeleted
        createdAt
        tags
       
      }
      pagination {
        limit
        offset
      }
      totalCount
    }
  }
`;

// Dedicated search query for searchUserInboxMessages function
const SEARCH_USER_INBOX_MESSAGES = gql`
  query SearchUserInboxMessages(
    $search: String!,
    $limit: Int,
    $offset: Int,
    $filter: String
  ) {
    searchUserInboxMessages(
      search: $search,
      limit: $limit,
      offset: $offset,
      filter: $filter
    ) {
      messages {
        id
        senderId
        senderString
        recipientId
        recipientString
        subject
        isRead
        isPinned
        isDeleted
        createdAt
        tags
      }
      pagination {
        limit
        offset
      }
      totalCount
      searchTerm
    }
  }
`;

const LIST_SENT_MESSAGES = gql`
  query ListSentMessages(
    $search: String
    $limit: Int
    $offset: Int
  ) {
    listUserSentMessages(
      search: $search
      limit: $limit
      offset: $offset
    ) {
      messages {
        id
        senderId
        senderString
        recipientId
        recipientString
        subject
        body
        isRead
        isPinned
        isDeleted
        createdAt
        tags
      }
      pagination {
        limit
        offset
      }
      totalCount
    }
  }
`;

const GET_USER_TAGS = gql`
  query GetUserTags {
    getUserTags {
      addresses {
        name
        email
      }
      name
      tags
      rules
      
    }    
  }
`;

const MARK_READ = gql`mutation MarkRead($id: [ID!]) {
  markUserMessageRead(id: $id)
}`;

const DELETE_MESSAGE = gql`mutation DeleteMessage($id: ID!) {
  deleteUserMessage(id: $id)
}`;

const RESPOND_MESSAGE = gql`mutation RespondMessage($originalMessageId: ID!, $body: String!, $toRecipients: [String], $ccRecipients: [String]) {
  respondToAll(originalMessageId: $originalMessageId, body: $body, toRecipients: $toRecipients, ccRecipients: $ccRecipients) {
    message 
  }
}`;

const SEND_MESSAGE = gql`mutation SendMessage($recipientId: ID!, $subject: String!, $body: String!) {
  createUserMessage(recipientId: $recipientId, subject: $subject, body: $body) {
    id senderId senderString recipientId subject body createdAt isRead isPinned isDeleted
  }
}`;

const UPDATE_USER = gql`mutation UpdateUser($updates: EmailUserDetailsInput!) {
  updateUser(updates: $updates) {
    addresses{
    name
    email
}
    tags
    rules
  }
}`;

// Types
interface UserMessage {
  id: string;
  senderId?: number | null;
  senderString?: string | null;
  recipientId: number;
  subject: string;
  body: string;
  isRead: boolean;
  isPinned: boolean;
  isDeleted: boolean;
  createdAt: string;
}

interface ListInboxData {
  listUserInboxMessages: {
    messages: UserMessage[];
    pagination: { limit: number; offset: number };
    totalCount: number;
  };
  listUserSentMessages: {
    messages: UserMessage[];
    pagination: { limit: number; offset: number };
    totalCount: number;
  };
  searchUserInboxMessages: {
    messages: UserMessage[];
    pagination: { limit: number; offset: number };
    totalCount: number;
  };
}

interface MarkReadVars { id: string; token?: string; }
interface DeleteMessageVars { id: string; }
interface RespondMessageVars { originalMessageId: string; body: string; ccRecipients: any, toRecipients: any }
interface SendMessageVars { recipientId: string; subject: string; body: string; }


// Entry via Auth
export const UserMessageByAuth: React.FC = () => {
  const { user } = useUser();

  const { data: userData, loading } = useQuery<{
    getUserTags: { name: string; tags: string }
  }>(GET_USER_TAGS);

  const [listInbox] = useLazyQuery<ListInboxData>(LIST_INBOX, {
    fetchPolicy: 'network-only',
  });
  const [searchInbox] = useLazyQuery<ListInboxData>(SEARCH_USER_INBOX_MESSAGES, {
    fetchPolicy: 'network-only',
  });
  const [listSent] = useLazyQuery<ListInboxData>(LIST_SENT_MESSAGES, {
    fetchPolicy: 'network-only',
  });
  const [markRead] = useMutation<void, MarkReadVars>(MARK_READ);
  const [deleteMessage] = useMutation<boolean, DeleteMessageVars>(DELETE_MESSAGE);
  const [respondMessage] = useMutation<{ respondToUserMessage: UserMessage }, RespondMessageVars>(RESPOND_MESSAGE);
  const [sendMessage] = useMutation<{ createUserMessage: UserMessage }, SendMessageVars>(SEND_MESSAGE);
  const [getThread] = useLazyQuery<any, any>(GET_USER_MESSAGE, {
    fetchPolicy: 'network-only',
  });
  const [updateUser] = useMutation<any, any>(UPDATE_USER);

  const hooks = {
    listInbox: async (search: string, limit: number, offset: number, filter: string) => { 
      try {
        // Parse the filter to get folder and subfolder
        const [folder] = filter.split(':');
        
        // Handle sent messages
        if (folder === 'sent') {
          console.log('Using listUserSentMessages query');
          const result = await listSent({ variables: { search, limit, offset } });
          console.log('Sent result:', result);
          return result;
        }
        
        // Handle inbox messages
        // If there's a search term, use the dedicated search function
        if (search && search.trim()) {
          console.log('Using searchUserInboxMessages query with term:', search.trim());
          const result = await searchInbox({ variables: { search: search.trim(), limit, offset, filter } });
          console.log('Search result:', result);
          return result;
        }
        // Otherwise use the regular list function
        console.log('Using regular listUserInboxMessages query');
        const result = await listInbox({ variables: { search, limit, offset, filter } });
        console.log('List result:', result);
        return result;
      } catch (error) {
        console.error('Error in listInbox hook:', error);
        throw error;
      }
    },
    markRead: async (id: string) => markRead({ variables: { id } }),
    deleteMessage: async (id: string) => deleteMessage({ variables: { id } }),
    respondMessage: async (id: string, body: string, toRecipients: string[], ccRecipients: string[]) => respondMessage({ variables: { originalMessageId: id, body, toRecipients, ccRecipients } }),
    sendMessage: async (rid: string, subject: string, body: string) => sendMessage({ variables: { recipientId: rid, subject, body } }),
    getThread: async (id: string) => getThread({ variables: { id, markRead: true }, fetchPolicy: 'network-only' }),
    updateUser: async (updates: any) => updateUser({ variables: { updates } }),
  };

  const fallbackUserEmail = `${user?.email || user?.username || ''}`.trim();
  const mailProfile = userData?.getUserTags || {
    addresses: [],
    name: fallbackUserEmail,
    rules: '[]',
    tags: '{}',
  };
  const mailUserEmail = mailProfile.name || fallbackUserEmail;

  if (loading) return <div className="p-4 text-sm">Loading user…</div>;

  return (
    <UserMessages
      userData  = {mailProfile}
      userEmail={mailUserEmail}
      userTags={mailProfile.tags}
      hooks={hooks}

    />
  );
};

// Entry via Token
